import { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, Receipt, ExternalLink } from 'lucide-react';
import {
  fetchInvoicesLedger,
  InvoiceLedgerEntry,
  InvoiceLedgerMode,
  InvoicePaymentState,
  PAYMENT_STATE_STYLES,
  PAYMENT_STATE_LABELS,
  getTermLabel,
} from '../../lib/invoicesLedger';
import { parseCalendarDate } from '../../lib/invoiceDates';
import { listStandaloneInvoices, type InvoiceRow } from '../../lib/invoices/standaloneInvoices';
import { fetchRentalInvoiceMap } from '../../lib/rentals';
import { fetchQuickBooksInvoiceStatuses, quickBooksSyncLabel, type QuickBooksInvoiceSync } from '../../lib/quickbooksSync';

type DateFilter = 'this-month' | 'last-month' | 'this-year' | 'all-time';

interface InvoicesLedgerTabProps {
  businessId: string;
  /** Completed bookings (legacy source). Bookings without a new invoice fall back to these so past invoices never disappear. */
  bookings?: any[];
  /** Legacy product sales (kept in the list until they move to the invoice system). */
  productSales?: any[];
  refreshKey?: number;
  onOpenBookingInvoice?: (bookingId: string) => void;
  onRecordPayment?: (bookingId: string) => void;
  onOpenProductSale?: (sale: any) => void;
  onApplyProductSalePayment?: (sale: any) => void;
  /** Quick-record a payment against a standalone invoice (counter sales / orders / manual invoices). */
  onRecordStandalonePayment?: (invoice: any) => void;
  /** Open a standalone (booking-less) invoice (status lets the caller pick editor vs viewer). */
  onOpenStandaloneInvoice?: (invoiceId: string, status: string) => void;
  /** Start creating a new standalone invoice. */
  onNewInvoice?: () => void;
}

function standaloneInvoiceToRow(inv: InvoiceRow): Row {
  const state: InvoicePaymentState =
    inv.status === 'void' ? 'void'
    : inv.status === 'paid' ? 'paid'
    : inv.status === 'partially_paid' ? 'partial'
    : inv.amount_due_cents > 0 ? 'unpaid' : 'paid';
  return {
    id: inv.id,
    kind: 'invoice',
    number: inv.invoice_number || '—',
    bookingNumber: null,
    customerName: inv.bill_to_name || 'No customer',
    // Full ISO timestamp — parseCalendarDate converts to the LOCAL calendar day.
    // Slicing the UTC string here made evening invoices "tomorrow" and the
    // date filter hid them.
    date: inv.finalized_at || inv.created_at || null,
    subtotal: inv.subtotal_cents / 100,
    discount: inv.discount_cents / 100,
    tax: inv.tax_cents / 100,
    total: inv.total_cents / 100,
    paid: inv.amount_paid_cents / 100,
    balance: inv.amount_due_cents / 100,
    taxLines: (inv.tax_breakdown || []).filter((c) => c.amount_cents !== 0).map((c) => ({ label: c.name, amount: c.amount_cents / 100 })),
    state,
    dueDate: inv.due_at || null,
    terms: inv.payment_terms,
    bookingId: null,
    raw: { ...inv, standalone: true },
  };
}

interface Row {
  id: string;
  kind: 'invoice' | 'product_sale';
  number: string;
  bookingNumber: string | null;
  customerName: string;
  date: string | null;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  paid: number;
  balance: number;
  taxLines: { label: string; amount: number }[];
  state: InvoicePaymentState;
  dueDate?: string | null;
  isOverdue?: boolean;
  terms?: string | null;
  bookingId?: string | null;
  raw: any;
}

const fmtMoney = (n: number) =>
  '$' + (Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtRateBps = (bps: number) => {
  const pct = (Number(bps) || 0) / 100;
  return Number.isInteger(pct) ? String(pct) : String(parseFloat(pct.toFixed(4)));
};

function getDateRange(filter: DateFilter): { start: Date | null; end: Date | null } {
  const now = new Date();
  const startOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 0, 0, 0, 0);
  const endOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 23, 59, 59, 999);
  switch (filter) {
    case 'this-month':
      return { start: startOfDay(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
    case 'last-month': {
      const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastDay = new Date(y, m + 1, 0).getDate();
      return { start: startOfDay(y, m, 1), end: endOfDay(y, m, lastDay) };
    }
    case 'this-year':
      return { start: startOfDay(now.getFullYear(), 0, 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
    case 'all-time':
    default:
      return { start: null, end: null };
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = parseCalendarDate(iso) || new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function invoiceToRow(e: InvoiceLedgerEntry): Row {
  const taxLines = (e.tax_breakdown || [])
    .filter((c) => c.amount_cents !== 0)
    .map((c) => ({
      label:
        c.base_cents != null
          ? `${c.name} (${fmtRateBps(c.rate_bps)}% on ${fmtMoney(c.base_cents / 100)})`
          : `${c.name} (${fmtRateBps(c.rate_bps)}%)`,
      amount: c.amount_cents / 100,
    }));
  return {
    id: e.id,
    kind: 'invoice',
    number: e.invoice_number || (e.booking_number != null ? String(e.booking_number) : '—'),
    bookingNumber: e.booking_number != null ? String(e.booking_number) : null,
    customerName: e.customer_name || 'Unknown',
    date: e.invoice_date,
    subtotal: e.subtotal_cents / 100,
    discount: e.discount_cents / 100,
    tax: e.tax_cents / 100,
    total: e.total_cents / 100,
    paid: e.amount_paid_cents / 100,
    balance: e.amount_due_cents / 100,
    taxLines,
    state: e.payment_state,
    dueDate: e.due_at,
    isOverdue: e.is_overdue,
    terms: e.payment_terms,
    bookingId: e.booking_id,
    raw: e,
  };
}

/** Legacy completed booking (no new invoice row) → uses booking totals + payments. */
function legacyBookingToRow(b: any): Row {
  const completed = (b.payments || []).filter((p: any) => p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid');
  const paid = completed.reduce(
    (s: number, p: any) => s + Math.max(0, (Number(p.amount) || 0) - (Number(p.surcharge_amount) || 0) - (Number(p.tip_amount) || 0)),
    0,
  );
  const total = Number(b.total_amount) || 0;
  const balance = Math.max(0, Math.round((total - paid) * 100) / 100);
  let state: InvoicePaymentState = 'unpaid';
  if (balance <= 0 && paid > 0) state = 'paid';
  else if (paid > 0) state = 'partial';
  const tax = Number(b.tax_amount) || 0;
  const customer = b.customer ? `${b.customer.first_name || ''} ${b.customer.last_name || ''}`.trim() : 'Unknown';
  return {
    id: b.id,
    kind: 'invoice',
    number: b.booking_number != null ? String(b.booking_number) : '—',
    bookingNumber: b.booking_number != null ? String(b.booking_number) : null,
    customerName: customer || 'Unknown',
    date: b.end_date || b.created_at,
    subtotal: Number(b.subtotal) || 0,
    discount: Number(b.discount_amount) || 0,
    tax,
    total,
    paid: Math.round(paid * 100) / 100,
    balance,
    taxLines: tax > 0 ? [{ label: 'Tax', amount: tax }] : [],
    state,
    bookingId: b.id,
    raw: b,
  };
}

function productSaleToRow(sale: any): Row {
  const completed = (sale.payments || []).filter((p: any) => p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid');
  const paid = completed.reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0);
  const total = Number(sale.total_amount) || 0;
  const balance = Math.max(0, Math.round((total - paid) * 100) / 100);
  let state: InvoicePaymentState = 'unpaid';
  if (sale.payment_status === 'paid' || (balance <= 0 && paid > 0)) state = 'paid';
  else if (paid > 0 || sale.payment_status === 'partial') state = 'partial';
  const tax = Number(sale.tax_amount) || 0;
  const customer = sale.customer ? `${sale.customer.first_name || ''} ${sale.customer.last_name || ''}`.trim() : 'Unknown';
  return {
    id: sale.id,
    kind: 'product_sale',
    number: sale.sale_number != null ? String(sale.sale_number) : '—',
    bookingNumber: null,
    customerName: customer || 'Unknown',
    date: sale.created_at,
    subtotal: Number(sale.subtotal_amount) || 0,
    discount: Number(sale.discount_amount) || 0,
    tax,
    total,
    paid,
    balance,
    taxLines: tax > 0 ? [{ label: `Tax${sale.tax_rate ? ` (${sale.tax_rate}%)` : ''}`, amount: tax }] : [],
    state,
    raw: sale,
  };
}

export default function InvoicesLedgerTab({
  businessId,
  bookings = [],
  productSales = [],
  refreshKey,
  onOpenBookingInvoice,
  onRecordPayment,
  onOpenProductSale,
  onApplyProductSalePayment,
  onRecordStandalonePayment,
  onOpenStandaloneInvoice,
  onNewInvoice,
}: InvoicesLedgerTabProps) {
  const [invoices, setInvoices] = useState<InvoiceLedgerEntry[]>([]);
  const [standalone, setStandalone] = useState<InvoiceRow[]>([]);
  // invoice id → rental numbers billed on it (R-0001…) — badge + searchable.
  const [rentalMap, setRentalMap] = useState<Map<string, string[]>>(new Map());
  const [qbSync, setQbSync] = useState<Map<string, QuickBooksInvoiceSync>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<InvoiceLedgerMode>('final');
  const [dateFilter, setDateFilter] = useState<DateFilter>('this-month');
  const [statusFilter, setStatusFilter] = useState<'all' | InvoicePaymentState>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchInvoicesLedger(businessId, { mode }),
      listStandaloneInvoices(businessId).catch(() => [] as InvoiceRow[]),
      fetchRentalInvoiceMap(businessId).catch(() => new Map<string, string[]>()),
    ])
      .then(async ([data, standaloneData, rentals]) => {
        if (!active) return;
        setInvoices(data);
        setStandalone(standaloneData);
        setRentalMap(rentals);
        const ids = [...data.map((row) => row.id), ...standaloneData.map((row) => row.id)];
        try {
          setQbSync(await fetchQuickBooksInvoiceStatuses(businessId, ids));
        } catch {
          setQbSync(new Map());
        }
      })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load invoices'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, refreshKey, mode]);

  const allRows = useMemo<Row[]>(() => {
    let rows: Row[];
    if (mode === 'drafts') {
      // Drafts are new-system invoices for not-yet-completed bookings only.
      rows = invoices.map(invoiceToRow);
      // Standalone draft invoices.
      rows = rows.concat(standalone.filter((s) => s.status === 'draft').map(standaloneInvoiceToRow));
    } else if (mode === 'void') {
      // Voided invoices — includes ones from deleted bookings (deleting a
      // booking voids its unpaid issued invoice; the record must stay visible).
      // Non-booking voids (orders, counter sales…) come from BOTH sources, so
      // prefer the standalone row (it's openable) and dedupe the ledger copy.
      rows = standalone.filter((s) => s.status === 'void').map(standaloneInvoiceToRow);
      const seen = new Set(rows.map((r) => r.id));
      rows = rows.concat(invoices.filter((inv) => !seen.has(inv.id)).map(invoiceToRow));
    } else {
      // Final: every completed booking shows — use its new invoice when one exists,
      // otherwise fall back to legacy booking totals so past invoices never disappear.
      const invoiceByBooking = new Map<string, InvoiceLedgerEntry>();
      for (const inv of invoices) {
        if (inv.booking_id) invoiceByBooking.set(inv.booking_id, inv);
      }
      rows = (bookings || []).flatMap((b: any) => {
        const inv = invoiceByBooking.get(b.id);
        // Voided invoices live under the Void toggle — and must NOT fall back
        // to legacy booking totals here, or the void would look active again.
        if (inv?.status === 'void') return [];
        return [inv ? invoiceToRow(inv) : legacyBookingToRow(b)];
      });
      // Invoice-backed sales (counter sales / orders) are represented by their
      // real invoice below — listing the product_sales companion row too would
      // double-list and double-count them. Only legacy pre-invoice sales show.
      rows = rows.concat((productSales || []).filter((s: any) => !s.invoice_id).map(productSaleToRow));
      // Standalone issued invoices (voids live under the Void toggle).
      rows = rows.concat(standalone.filter((s) => s.status !== 'draft' && s.status !== 'void').map(standaloneInvoiceToRow));
    }
    return rows.sort((a, b) => {
      const da = parseCalendarDate(a.date)?.getTime() ?? 0;
      const db = parseCalendarDate(b.date)?.getTime() ?? 0;
      return db - da;
    });
  }, [invoices, standalone, bookings, productSales, mode]);

  const openRow = (r: Row) => {
    if (r.kind === 'invoice' && (r.raw as any)?.standalone) onOpenStandaloneInvoice?.(r.id, (r.raw as any)?.status);
    else if (r.kind === 'invoice' && r.bookingId) onOpenBookingInvoice?.(r.bookingId);
    else if (r.kind === 'product_sale') onOpenProductSale?.(r.raw);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const { start, end } = getDateRange(dateFilter);
    return allRows.filter((r) => {
      // Date range only applies to Final invoices; drafts are state-based, not period-based.
      if (mode === 'final' && start && end) {
        const d = parseCalendarDate(r.date);
        if (!d || d < start || d > end) return false;
      }
      if (mode !== 'void' && statusFilter !== 'all' && r.state !== statusFilter) return false;
      if (q) {
        const rentalNums = r.kind === 'invoice' ? (rentalMap.get(r.id) || []) : [];
        const hay = [r.customerName, r.number, String(r.total), ...rentalNums].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allRows, dateFilter, statusFilter, search, mode, rentalMap]);

  const summary = useMemo(() => {
    let invoiced = 0, collected = 0, outstanding = 0;
    for (const r of filtered) {
      if (mode === 'void') {
        // Voided invoices owe nothing — show what was voided, not receivables.
        invoiced += r.total;
        collected += r.paid;
        continue;
      }
      if (r.state === 'void') continue;
      invoiced += r.total;
      collected += r.paid;
      outstanding += r.balance;
    }
    return { invoiced, collected, outstanding, count: filtered.length };
  }, [filtered, mode]);

  return (
    <div className="pb-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
      {/* Final / Drafts toggle + New Invoice */}
      <div className="px-4 pt-4 flex items-center justify-between gap-3">
        <div className="inline-flex bg-gray-100 rounded-[10px] p-1 gap-1">
          {([['final', 'Final'], ['drafts', 'Drafts'], ['void', 'Void']] as const).map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setMode(val)}
              className={`px-4 py-1.5 text-[13px] font-semibold rounded-[8px] transition-all ${
                mode === val ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]' : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {label}
            </button>
          ))}
        </div>
        {onNewInvoice && (
          <button
            type="button"
            onClick={onNewInvoice}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shadow-sm"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Receipt className="h-4 w-4" /> New Invoice
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 pt-3 pb-2">
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Invoices</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{summary.count}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Invoiced</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{fmtMoney(summary.invoiced)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Collected</p>
          <p className="text-[16px] font-bold text-green-600 truncate">{fmtMoney(summary.collected)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Outstanding</p>
          <p className="text-[16px] font-bold text-red-600 truncate">{fmtMoney(summary.outstanding)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, invoice #, rental # (R-0001), amount"
            className="w-full pl-9 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
          />
        </div>
        {mode === 'final' && (
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value as DateFilter)}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="this-year">This Year</option>
            <option value="all-time">All Time</option>
          </select>
        )}
        {/* Everything under the Void toggle is void — no status filter needed there */}
        {mode !== 'void' && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="unpaid">Unpaid</option>
          </select>
        )}
      </div>

      {/* List */}
      <div className="px-4">
        {loading ? (
          <div className="py-16 text-center text-gray-500 text-sm">Loading invoices…</div>
        ) : error ? (
          <div className="py-10 text-center text-red-600 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No invoices for this period.</p>
          </div>
        ) : (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
            {filtered.map((r) => {
              const expanded = expandedId === r.id;
              const stateClass = PAYMENT_STATE_STYLES[r.state] || 'bg-gray-100 text-gray-600';
              return (
                <div key={`${r.kind}-${r.id}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpandedId(expanded ? null : r.id); } }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-gray-900 truncate">{r.customerName}</span>
                        <span className="text-[12px] text-gray-500 truncate">
                          · {r.kind === 'invoice' ? `Invoice #${r.number}` : `Sale #${r.number}`}
                          {r.kind === 'invoice' && r.bookingNumber && r.bookingNumber !== r.number
                            ? ` · Booking #${r.bookingNumber}`
                            : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[12px] text-gray-500">{formatDate(r.date)}</span>
                        {r.dueDate && (
                          <span className={`text-[11px] ${r.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                            {r.isOverdue ? 'Overdue · ' : 'Due '}{formatDate(r.dueDate)}
                          </span>
                        )}
                        {r.kind === 'product_sale' && (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-purple-50 text-[11px] font-medium text-purple-600">Product</span>
                        )}
                        {r.kind === 'invoice' && quickBooksSyncLabel(qbSync.get(r.id)) && (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${qbSync.get(r.id)?.status === 'error' ? 'bg-red-50 text-red-700' : qbSync.get(r.id)?.status === 'synced' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {quickBooksSyncLabel(qbSync.get(r.id))}
                          </span>
                        )}
                        {r.kind === 'invoice' && (rentalMap.get(r.id)?.length ?? 0) > 0 && (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-[11px] font-medium text-indigo-600">
                            Rental {rentalMap.get(r.id)!.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[15px] font-bold text-gray-900">{fmtMoney(r.total)}</div>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${stateClass}`}>{PAYMENT_STATE_LABELS[r.state]}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openRow(r);
                      }}
                      className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0 transition-colors"
                      title="Open invoice"
                      aria-label="Open invoice"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>

                  {expanded && (
                    <div className="px-4 pb-3 pl-11 text-[12px] animate-[fadeIn_0.12s_ease-out]">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        <KV label="Subtotal" value={fmtMoney(r.subtotal)} />
                        {r.discount > 0 && <KV label="Discount" value={`−${fmtMoney(r.discount)}`} valueClass="text-red-600 font-medium" />}
                        {r.taxLines.map((t, i) => <KV key={i} label={t.label} value={fmtMoney(t.amount)} />)}
                        {r.taxLines.length === 0 && r.tax > 0 && <KV label="Tax" value={fmtMoney(r.tax)} />}
                        <KV label="Total" value={fmtMoney(r.total)} valueClass="text-gray-900 font-semibold" />
                        <KV label="Paid" value={fmtMoney(r.paid)} valueClass="text-green-600 font-medium" />
                        <KV label="Balance" value={fmtMoney(r.balance)} valueClass={r.balance > 0 ? 'text-red-600 font-semibold' : 'text-gray-900 font-medium'} />
                        {r.terms && <KV label="Terms" value={getTermLabel(r.terms)} />}
                        {r.dueDate && <KV label="Due" value={formatDate(r.dueDate)} valueClass={r.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-900 font-medium'} />}
                        {r.kind === 'invoice' && qbSync.get(r.id)?.error && (
                          <KV label="QuickBooks" value={qbSync.get(r.id)!.error!} valueClass="text-red-600" />
                        )}
                      </div>

                      <div className="pt-2 flex items-center gap-4">
                        {r.kind === 'invoice' && (r.raw as any)?.standalone && onOpenStandaloneInvoice && (
                          <button type="button" onClick={() => onOpenStandaloneInvoice(r.id, (r.raw as any)?.status)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">{(r.raw as any)?.status === 'draft' ? 'Edit invoice →' : 'View invoice →'}</button>
                        )}
                        {r.kind === 'invoice' && (r.raw as any)?.standalone && r.balance > 0 && r.state !== 'void' && (r.raw as any)?.status !== 'draft' && onRecordStandalonePayment && (
                          <button type="button" onClick={() => onRecordStandalonePayment(r.raw)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">Record payment</button>
                        )}
                        {r.kind === 'invoice' && r.bookingId && onOpenBookingInvoice && (
                          <button type="button" onClick={() => onOpenBookingInvoice(r.bookingId!)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">View invoice →</button>
                        )}
                        {r.kind === 'invoice' && r.bookingId && r.balance > 0 && r.state !== 'void' && onRecordPayment && (
                          <button type="button" onClick={() => onRecordPayment(r.bookingId!)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">Record payment</button>
                        )}
                        {r.kind === 'product_sale' && onOpenProductSale && (
                          <button type="button" onClick={() => onOpenProductSale(r.raw)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">View →</button>
                        )}
                        {r.kind === 'product_sale' && r.balance > 0 && onApplyProductSalePayment && (
                          <button type="button" onClick={() => onApplyProductSalePayment(r.raw)} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">Apply payment</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value, valueClass = 'text-gray-900 font-medium' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className={`${valueClass} truncate`}>{value}</span>
    </div>
  );
}
